// deps
import React, { ReactElement, memo, useContext, useMemo, useCallback } from "react";

// internal-deps
import {
    AddressList,
    AddressListV2,
    AddressListErrorHalfCard,
    AddressListLoaderHalfCard,
    BharatMandapamSelectPickup,
} from "@swiggy-private/cart-ui";
import { AddressInfo, UserAddress } from "@swiggy-private/cart-types";
import { isUserLoggedIn } from "@cart-client/helpers/User";

// hooks
import { useFetchAddressOnClick } from "@cart-client/hooks/useFetchAddressOnClick";
import useFetchAddressList from "@cart-client/hooks/useFetchAddressList";

// interfaces
import { ReviewedCart } from "@cart-client/interfaces/Cart";

// contexts
import { CartAddressContext } from "@cart-client/contexts/CartAddressContext";
import { getHaversineDistance } from "@cart-client/helpers/CartAddress";
import { useCartAddressNudgeAnalytics } from "@cart-client/hooks/useCartAddressNudgeAnalytics";
import { isBharatMandapamCity } from "@cart-client/helpers/BharatMandapam";

export interface AddressListManagerProps {
    reviewedCart: ReviewedCart | null;
    displayAddressList: AddressInfo[] | UserAddress[];
}

function AddressListManager({ reviewedCart, displayAddressList }: AddressListManagerProps): ReactElement | null {
    const { selectedAddress, goToAddAddress, onSelectAddress, onAddressListClose } = useFetchAddressOnClick(
        reviewedCart?.data?.cart_total,
        reviewedCart?.data?.addresses,
    );
    const { cartAddressState } = useContext(CartAddressContext);
    const { showAddressList, isErrorFetchingAddressList, isFetchingAddressList, addressNudgeConfig } = cartAddressState;
    const { isAddressNudgeXpEnabled, currentLocation, showAddressNudgeIcon } = addressNudgeConfig;
    const userLoggedIn = isUserLoggedIn();

    const isBharatMandapam = useMemo(() => isBharatMandapamCity() && userLoggedIn, [userLoggedIn]);

    //find the index of selected address
    const selectedAddressIndex = useMemo(
        () => displayAddressList.findIndex((address: AddressInfo | UserAddress) => address.id === selectedAddress?.id),
        [displayAddressList, selectedAddress?.id],
    );
    //based on isAddressNudgeXpEnabled flag modifying displayAddressList
    const addressList = useMemo(() => {
        if (!isAddressNudgeXpEnabled || selectedAddressIndex === -1) return displayAddressList;

        //place selected address at the beginning of address list
        const list = [...displayAddressList];
        const [selectedAddressItem] = list.splice(selectedAddressIndex, 1);
        const finalAddressList = [selectedAddressItem, ...list];

        //for each address add isSelected and if current location is available then add haversine distance also
        return finalAddressList.map((address) => ({
            ...address,
            isSelected: address?.id === selectedAddress?.id,
            haversineDistance:
                currentLocation && currentLocation.lat && currentLocation.lng
                    ? getHaversineDistance(currentLocation, {
                          lat: address.lat as string,
                          lng: address.lng as string,
                      })
                    : undefined,
        }));
    }, [currentLocation, displayAddressList, isAddressNudgeXpEnabled, selectedAddress?.id, selectedAddressIndex]);

    //add cRank property, here cRank denotes the position of an address in the bottom sheet of checkout page
    const displayAddressListWithCRank = useMemo(
        () => [...addressList].map((address, position) => ({ ...address, cRank: position })),
        [addressList],
    );

    const { fetchAddressList } = useFetchAddressList();
    const { onImpressionBottomAddressList, onClickReloadAddresses, onImpressionBottomAddressListError } =
        useCartAddressNudgeAnalytics();

    const onReloadAddresses = useCallback(() => {
        onClickReloadAddresses();
        fetchAddressList();
    }, [fetchAddressList, onClickReloadAddresses]);

    if (showAddressList) {
        if (isBharatMandapam) {
            return (
                <BharatMandapamSelectPickup
                    isOpen={showAddressList}
                    onClose={onAddressListClose}
                    addresses={displayAddressListWithCRank as AddressInfo[]}
                    onSelectAddress={onSelectAddress}
                />
            );
        }

        return isAddressNudgeXpEnabled ? (
            <AddressListV2
                addresses={displayAddressListWithCRank}
                showAddressList={showAddressList}
                showAddressNudgeIcon={showAddressNudgeIcon}
                addNewAddress={goToAddAddress}
                selectAddress={onSelectAddress}
                onClose={onAddressListClose}
                onImpression={onImpressionBottomAddressList}
            />
        ) : (
            <AddressList
                addresses={displayAddressListWithCRank}
                selectedAddressIndex={selectedAddressIndex}
                addNewAddress={goToAddAddress}
                selectAddress={onSelectAddress}
                onClose={onAddressListClose}
                onImpression={onImpressionBottomAddressList}
            />
        );
    }

    if (isErrorFetchingAddressList) {
        return (
            <AddressListErrorHalfCard
                reloadAddresses={onReloadAddresses}
                addAddress={goToAddAddress}
                onImpression={onImpressionBottomAddressListError}
            />
        );
    }

    if (isFetchingAddressList) {
        return <AddressListLoaderHalfCard />;
    }

    return null;
}

export default memo(AddressListManager);
